import { makeAutoObservable, reaction } from "mobx";
import type RootStore from "./RootStore";
import type { ServiceStoreType } from "../types/ServiceStoreTypes";
import { registerPageAccessStore } from "./pageVisibilityGate";
import {
  isActiveDenyRule,
  matchesPermissionPath,
  type PageAccessRule,
} from "../shared/permissionPath";

export type { PageAccessRule };

/**
 * Holds the signed-in user's PAGE-VISIBILITY rules (their own `user-permissions`
 * rows) and answers "is this path hidden for me?".
 *
 * ─── THIS IS UX GATING, NOT A SECURITY BOUNDARY ───────────────────────────────
 *
 *   Page visibility hides pages in this app's UI. It is NOT a security
 *   boundary: what an account may read or change through the API is governed
 *   by its role/tier, which the server enforces on every request
 *   (`packages/api/src/services/_shared/rbac-tier.ts`).
 *
 * Deny rows are keyed by WEB ROUTE PATH (`layout + path`, e.g. "/app/
 * infrastructure"). There is no route→service mapping in this codebase and one
 * is deliberately NOT being invented: a guessed mapping would look
 * authoritative and be wrong at the edges, which is strictly worse than honest
 * UX gating. So NO API hook consults these rows — pinned by
 * `packages/api/src/services/user/permissions/page-visibility-boundary.test.ts`,
 * which goes red if anyone wires them server-side without updating this
 * comment and `packages/web/CLAUDE.md`.
 *
 * Consequence, stated plainly: a user with Infrastructure hidden who holds the
 * `operator` tier can still call `instance-actions` over REST/socket. That is
 * the intended contract, not a bug to paper over here.
 *
 * ─── SCOPE ────────────────────────────────────────────────────────────────────
 *
 * WHO CALLS THIS: `pageAccessRouteGuard` (blocks navigation) and
 * `stores/pageVisibilityGate.ts` (removes the page from the nav and the page
 * search). The nav wiring landed 2026-08-16: the gate wraps the composed route
 * list in `views/routesList.tsx` so each route's `hidden` also reflects these
 * rules. It MUST stay a getter and MUST compose with `billingGate`'s getter
 * rather than overwrite it — both traps are documented in that file.
 *
 * Model: default-VISIBLE with per-user deny rows. An admin hides a page for a
 * user by creating a `user-permissions` row with `isDenied: true` and
 * `location` = the route path (managed in Administration → Page Visibility).
 * Admin-tier users are never hidden from — that bypass lives in
 * `pageAccessRouteGuard`, not here, so this store stays a pure "what do the
 * rows say" derivation.
 *
 * ─── FAIL-OPEN, DELIBERATELY ──────────────────────────────────────────────────
 *
 * `loadOwnRules` swallows a failed load and leaves `deniedLocations` empty, so
 * a rules-load failure means NOTHING is hidden. For UX gating that is the right
 * default: the alternative — locking a user out of every page because one
 * request failed — turns a transient network blip into a total outage for that
 * user, and there is nothing to protect here anyway (the API is enforcing the
 * real boundary regardless of what this store believes). If this were a
 * security check, failing open on a load error would be unacceptable and the
 * correct behaviour would be to refuse until the rules are known.
 *
 * Rows live in the registered `user-permissions` ServiceStore's entity cache
 * (per the MobX rule); this store only derives over them. The API scopes
 * non-admin finds to the caller's own rows, so a plain find loads exactly the
 * signed-in user's rules.
 */
export class PageAccessStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // Publish this instance to the nav-visibility gate. The route list is built
    // at module-import time, long before any RootStore exists, so the gate's
    // `hidden` getter resolves the store lazily through here — the same shape
    // `views/billing/billingGate.ts` uses for capability gating.
    registerPageAccessStore(this);

    // Refresh the signed-in user's rules whenever the authenticated user
    // changes (login, refresh-restore, user switch).
    reaction(
      () => this.currentUserId,
      (userId) => {
        if (userId != null) void this.loadOwnRules();
      },
      { fireImmediately: true }
    );
  }

  /**
   * The built-in `user-permissions` ServiceStore the RootStore mounts itself.
   *
   * Read off the rootStore key rather than through
   * `serviceStoreManager.getStore("user-permissions")`: `userPermissionsStore`
   * is a RESERVED built-in key, so HQ must not (and no longer does) configure a
   * second store against that path — the manager's map is empty for it.
   */
  private get permissionsService(): ServiceStoreType | null {
    return (
      (this.rootStore.userPermissionsStore as unknown as ServiceStoreType | null) ??
      null
    );
  }

  get currentUserId(): number | string | null {
    const user = (
      this.rootStore as unknown as {
        authStore?: { currentUser?: { id?: number | string } };
      }
    ).authStore?.currentUser;
    return user?.id ?? null;
  }

  /**
   * Whether the signed-in user is admin-tier — the library `AuthStore`'s
   * `isAdminUser`, which mirrors `PermissionManager.isUserAdmin` (the check the
   * router refuses `requiresAdmin` routes with). Read by the nav gate so an
   * admin-only page leaves the nav for exactly the users the router would
   * refuse. False while nobody is signed in.
   */
  get isAdminUser(): boolean {
    return (
      (
        this.rootStore as unknown as {
          authStore?: { isAdminUser?: boolean };
        }
      ).authStore?.isAdminUser === true
    );
  }

  /** Load the signed-in user's own rules into the ServiceStore cache. */
  async loadOwnRules(): Promise<void> {
    const userId = this.currentUserId;
    const service = this.permissionsService;
    if (userId == null || !service) return;
    try {
      await service.find({ query: { userId, $limit: 5000 } });
    } catch (err) {
      // FAIL-OPEN #1 (deliberate — see the class docstring). A failed load
      // means no rows apply, so nothing is hidden. Correct for UX gating,
      // and would be unacceptable if this were a security check.
      console.warn("Failed to load page-visibility rules:", err);
    }
  }

  /** Hide-rule locations that apply to the signed-in user in this app. */
  get deniedLocations(): string[] {
    const userId = this.currentUserId;
    if (userId == null) return [];
    const appName = this.rootStore.appName;
    const rules = (this.permissionsService?.entities ??
      []) as unknown as PageAccessRule[];
    return rules
      .filter((rule) => isActiveDenyRule(rule, userId, appName))
      .map((rule) => String(rule.location || ""))
      .filter(Boolean);
  }

  /**
   * Whether a page path is HIDDEN for the signed-in user in this app's UI.
   * Never a statement about what the API will allow — see the class docstring.
   */
  isPathDenied(pagePath: string): boolean {
    if (!pagePath) return false;
    return this.deniedLocations.some((location) =>
      matchesPermissionPath(location, pagePath)
    );
  }
}

export default PageAccessStore;
